import client from './client';

// Thin wrappers that unwrap the standard envelope.
//   success: { success:true, data:..., requestId }
//   error  : { success:false, error:{code,message,details?}, requestId }
// Helpers return data on success; on error they throw the axios error and
// callers use extractApiError() for display copy.

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

const endpoints = {
  auth: {
    masterLogin: ({ email, password }) =>
      client.post('/api/auth/master/login', { email, password }).then(unwrap),
    userLogin: ({ email, password }) =>
      client.post('/api/auth/user/login', { email, password }).then(unwrap),
    // Stage 5M: complete a deferred login by verifying the 6-digit TOTP
    // code from the master's Google Authenticator. Body shape:
    //   { challengeId: string, code: '123456' }
    // The server returns the same envelope as a successful login (access
    // token + refresh token + user). Errors map to MFA_INVALID_CODE,
    // MFA_TOO_MANY_ATTEMPTS, MFA_CHALLENGE_EXPIRED.
    loginMfaVerify: ({ challengeId, code }) =>
      client
        .post('/api/auth/login/mfa/verify', { challengeId, code })
        .then(unwrap),
    refresh: ({ refreshToken }) =>
      client.post('/api/auth/refresh', { refreshToken }).then(unwrap),
    logout: () => client.post('/api/auth/logout', {}).then(unwrap),
    me: () => client.get('/api/auth/me').then(unwrap),
    // Stage 5N-6: forgot/reset password. Server returns the generic
    // envelope on the request side regardless of whether the email
    // resolved to an account; the reset side returns explicit error
    // codes (RESET_TOKEN_INVALID, PASSWORD_POLICY_VIOLATION,
    // PASSWORD_CONFIRM_MISMATCH) since the caller already holds a link.
    forgotPassword: ({ email }) =>
      client.post('/api/auth/forgot-password', { email }).then(unwrap),
    resetPassword: ({ token, newPassword, confirmPassword }) =>
      client
        .post('/api/auth/reset-password', { token, newPassword, confirmPassword })
        .then(unwrap),
    session: {
      current: () => client.get('/api/auth/session').then(unwrap),
      events: ({ page = 1, pageSize = 20 } = {}) =>
        client
          .get('/api/auth/session/events', { params: { page, pageSize } })
          .then(unwrap)
    }
  },

  signup: {
    start: (body) => client.post('/api/signup/start', body).then(unwrap),
    verifyMobileOtp: (body) =>
      client.post('/api/signup/verify-mobile-otp', body).then(unwrap),
    verifyEmailOtp: (body) =>
      client.post('/api/signup/verify-email-otp', body).then(unwrap),
    status: (id) => client.get(`/api/signup/status/${id}`).then(unwrap),
    // Stage 5N-4: read the live Standard Plan pricing config so the
    // SignupPaymentPage doesn't hardcode ₹1,999 or 18%. Returns
    // customer-safe fields only.
    pricingConfig: () => client.get('/api/signup/pricing-config').then(unwrap),
    quote: (body) => client.post('/api/signup/quote', body).then(unwrap),
    // Stage 5J-D: callers MUST include `clientAttemptId` (a 20-100 char
    // [A-Za-z0-9_-] token, typically crypto.randomUUID()) so the backend
    // dedupes Razorpay create-order across double-clicks and refreshes.
    createOrder: (body) => client.post('/api/signup/create-order', body).then(unwrap),
    verifyPayment: (body) =>
      client.post('/api/signup/verify-payment', body).then(unwrap),
    // Stage 5J-D: post-Razorpay-capture recovery. Body shape:
    //   { pendingSignupId, masterUsername, masterPassword,
    //     razorpayOrderId? , razorpayPaymentId? }   (one of order/payment id required)
    // No signature is sent — backend re-fetches from Razorpay and asserts
    // amount/currency/order_id match before provisioning. Safe by design.
    recoverPayment: (body) =>
      client.post('/api/signup/recover-payment', body).then(unwrap),
    // Stage 5K-1: read-only coupon preview. Body:
    //   { pendingSignupId, couponCode }
    // Returns custom-plan details when the coupon is valid; throws on
    // backend rejection. Caller maps the error via parseApiError.
    previewCouponPlan: (body) =>
      client.post('/api/signup/preview-coupon-plan', body).then(unwrap),
    // Stage 5K-2: free trial activation. Body:
    //   { pendingSignupId, masterUsername, masterPassword }
    // Provisions an account on FREE_TRIAL_7D — no Razorpay order, no
    // payment transaction, no paid invoice. Idempotent: re-issuing the
    // same request returns the previously-provisioned account.
    activateFreeTrial: (body) =>
      client.post('/api/signup/activate-free-trial', body).then(unwrap),
    // Stage 5K-3: no-payment coupon-plan account activation. Body:
    //   { pendingSignupId, couponCode, masterUsername, masterPassword }
    // For paid coupons, the frontend uses signup.quote + create-order +
    // verify-payment instead. Backend detects the applied coupon via
    // selected_coupon_code on pending_signups and routes to the
    // coupon-aware provisioner.
    activateCouponPlan: (body) =>
      client.post('/api/signup/activate-coupon-plan', body).then(unwrap)
  },

  invoices: {
    list: ({ page = 1, pageSize = 20 } = {}) =>
      client
        .get('/api/subscription/invoices', { params: { page, pageSize } })
        .then(unwrap),
    get: (id) => client.get(`/api/subscription/invoices/${id}`).then(unwrap)
    // The legacy `viewReport(id)` helper used `/api/subscription/invoices/:id/report`
    // (the dedicated text/html viewer). It was removed in the R3 follow-up;
    // every tenant invoice view now goes through `endpoints.reports.createJob`
    // → `/reports/jobs/:jobId`. The backend route is preserved for ops/curl.
  },

  subscription: {
    get: () => client.get('/api/subscription').then(unwrap),
    seats: {
      quote: ({ additionalSeats }) =>
        client
          .post('/api/subscription/seats/quote', { additionalSeats })
          .then(unwrap),
      createOrder: ({ additionalSeats }) =>
        client
          .post('/api/subscription/seats/create-order', { additionalSeats })
          .then(unwrap),
      verifyPayment: ({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) =>
        client
          .post('/api/subscription/seats/verify-payment', {
            razorpayOrderId,
            razorpayPaymentId,
            razorpaySignature
          })
          .then(unwrap)
    },
    // Stage 5N-8: subscription renewal payment flow. Master-only.
    // Reachable even when subscription is expired (the login gate is
    // soft for masters; subscriptionEnforce is not mounted on this
    // route group). Mirrors the seats addon shape.
    renewal: {
      quote: ({ billingCycle, userCount }) =>
        client
          .post('/api/subscription/renewal/quote', { billingCycle, userCount })
          .then(unwrap),
      createOrder: ({ billingCycle, userCount }) =>
        client
          .post('/api/subscription/renewal/create-order', { billingCycle, userCount })
          .then(unwrap),
      verifyPayment: ({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) =>
        client
          .post('/api/subscription/renewal/verify-payment', {
            razorpayOrderId,
            razorpayPaymentId,
            razorpaySignature
          })
          .then(unwrap)
    }
  },

  users: {
    list: () => client.get('/api/users').then(unwrap),
    create: (body) => client.post('/api/users', body).then(unwrap),
    deactivate: (id) =>
      client.post(`/api/users/${id}/deactivate`, {}).then(unwrap),
    // Stage 5K-5A: lifecycle actions. Body is `{ reason?: string }`.
    lock: (id, body = {}) =>
      client.post(`/api/users/${id}/lock`, body).then(unwrap),
    unlock: (id, body = {}) =>
      client.post(`/api/users/${id}/unlock`, body).then(unwrap),
    surrender: (id, body = {}) =>
      client.post(`/api/users/${id}/surrender`, body).then(unwrap),
    // Stage 5K-5B: profile + password + activity.
    updateProfile: (id, body) =>
      client.patch(`/api/users/${id}/profile`, body).then(unwrap),
    adminResetPassword: (id, body) =>
      client.post(`/api/users/${id}/reset-password`, body).then(unwrap),
    selfChangePassword: (body) =>
      client.post('/api/users/me/change-password', body).then(unwrap),
    activity: (id, { limit = 20 } = {}) =>
      client.get(`/api/users/${id}/activity`, { params: { limit } }).then(unwrap),
    // Stage 5K-5B: self-profile surface (reachable by sub-users too).
    getMyProfile: () => client.get('/api/users/me/profile').then(unwrap),
    updateMyProfile: (body) =>
      client.patch('/api/users/me/profile', body).then(unwrap),
    myActivity: ({ limit = 20 } = {}) =>
      client.get('/api/users/me/activity', { params: { limit } }).then(unwrap),
    // Stage 5L: per-user login IP access control.
    //   getIpAccess(id)         → { userId, ipAccessMode, whitelist[] }
    //   updateIpAccess(id, body) → same shape after save.
    // Body: { ipAccessMode: 'any' | 'whitelist',
    //         whitelist: [{ ipAddress, label?, isActive? }] }
    getIpAccess: (id) =>
      client.get(`/api/users/${id}/ip-access`).then(unwrap),
    updateIpAccess: (id, body) =>
      client.put(`/api/users/${id}/ip-access`, body).then(unwrap),
    // Stage 5M: per-user MFA requirement toggle. Master-only.
    //   body: { mfaRequired: boolean }
    setMfaRequired: (id, body) =>
      client.put(`/api/users/${id}/mfa-required`, body).then(unwrap)
  },

  // Stage 5M: master-controlled tenant MFA setup. Master-only.
  //   getStatus()                      → { isSetupComplete, isActive, hasSecret, ... }
  //   beginSetup()                     → { qrDataUrl, manualEntryKey, issuer, accountLabel }
  //   verifySetup({ code })            → { isSetupComplete, isActive }
  //   setActive({ isActive: boolean }) → { isActive }
  mfa: {
    getStatus: () => client.get('/api/mfa/status').then(unwrap),
    beginSetup: () => client.post('/api/mfa/setup/begin', {}).then(unwrap),
    verifySetup: ({ code }) =>
      client.post('/api/mfa/setup/verify', { code }).then(unwrap),
    setActive: ({ isActive }) =>
      client.put('/api/mfa/active', { isActive }).then(unwrap),
    // Stage 5M-Recovery — recovery codes (master-only).
    //   getRecoveryCodesStatus()  → { unused, used, superseded, lastGeneratedAt, perBatch }
    //   regenerateRecoveryCodes() → { generationId, codes: [{ id, plaintext, generatedAt }] }
    // The plaintext field is only present in the regenerate response;
    // /status NEVER returns plaintext or hashes.
    getRecoveryCodesStatus: () =>
      client.get('/api/mfa/recovery-codes/status').then(unwrap),
    regenerateRecoveryCodes: () =>
      client.post('/api/mfa/recovery-codes/regenerate', {}).then(unwrap)
  },

  // Approval MFA — step-up authentication. SEPARATE from login MFA.
  //   createChallenge({ operationKey, resourceType, resourceId, metadata? })
  //                              → { challengeId, requiredLevel, ... }
  //   verifyL1({ challengeId, mfaCode })
  //                              → { l1Status: 'verified', ... }
  //   verifyL2({ challengeId, mfaCode })
  //                              → { l2Status: 'verified', ... }
  //   getChallenge(challengeId)  → current challenge state
  // Demo (mock) protected operations live under the same router so the
  // ERP grid demo can drive end-to-end approval flows without needing
  // real production modules wired up:
  //   demoCustomerDelete(id, { approvalMfaChallengeId })
  //   demoBulkDelete({ ids, approvalMfaChallengeId })
  approvalMfa: {
    createChallenge: ({ operationKey, resourceType, resourceId, metadata }) =>
      client
        .post('/api/approval-mfa/challenge', {
          operationKey, resourceType, resourceId, metadata
        })
        .then(unwrap),
    verifyL1: ({ challengeId, mfaCode }) =>
      client
        .post('/api/approval-mfa/verify-l1', { challengeId, mfaCode })
        .then(unwrap),
    verifyL2: ({ challengeId, mfaCode }) =>
      client
        .post('/api/approval-mfa/verify-l2', { challengeId, mfaCode })
        .then(unwrap),
    getChallenge: (challengeId) =>
      client
        .get(`/api/approval-mfa/challenges/${encodeURIComponent(challengeId)}`)
        .then(unwrap),
    demoCustomerDelete: (id, { approvalMfaChallengeId }) =>
      client
        .post(
          `/api/approval-mfa/demo/customers/${encodeURIComponent(id)}/delete`,
          { approvalMfaChallengeId }
        )
        .then(unwrap),
    demoBulkDelete: ({ ids, approvalMfaChallengeId }) =>
      client
        .post('/api/approval-mfa/demo/bulk-delete', {
          ids, approvalMfaChallengeId
        })
        .then(unwrap)
  },

  // Stage R1: reusable report engine.
  //   listDefinitions()              -> { definitions: [...] }
  //   listJobs({limit, offset})      -> { jobs: [...], limit, offset }
  //   getJob(jobId)                  -> { job }
  //   createJob({reportCode, format, params})
  //                                  -> { job }
  //   cancelJob(jobId)               -> { job }
  //   emailJob(jobId, { recipient }) -> { ok, jobId, recipient }
  //   resultUrl(jobId)               -> path string used by the iframe
  //                                     viewer's `src` (the response is
  //                                     text/html, not JSON).
  reports: {
    listDefinitions: () =>
      client.get('/api/reports/definitions').then(unwrap),
    // Stage R2: engine capability snapshot. The frontend reads this
    // once on report-list mount to render PDF-disabled / SMTP states.
    capabilities: () =>
      client.get('/api/reports/capabilities').then(unwrap),
    listJobs: ({ limit = 50, offset = 0 } = {}) =>
      client.get('/api/reports/jobs', { params: { limit, offset } }).then(unwrap),
    getJob: (jobId) =>
      client.get(`/api/reports/jobs/${encodeURIComponent(jobId)}`).then(unwrap),
    // Stage R3: accept either `inputParams` (preferred wire name —
    // mirrors the report_jobs.input_params column) or legacy
    // `params`. Backend validator accepts both shapes.
    createJob: ({ reportCode, format, params, inputParams }) => {
      const body = {
        reportCode,
        ...(format ? { format } : {}),
        // Send under the `inputParams` key when caller provided it
        // (so the wire body matches the spec); otherwise keep the
        // legacy `params` shape so older callers don't break.
        ...(inputParams !== undefined
          ? { inputParams }
          : { params: params || {} })
      };
      // No console logging here — request bodies / responses must not be
      // written to the browser console (they can carry sensitive payloads).
      return client.post('/api/reports/jobs', body).then((resp) => unwrap(resp));
    },
    cancelJob: (jobId) =>
      client.post(`/api/reports/jobs/${encodeURIComponent(jobId)}/cancel`, {}).then(unwrap),
    emailJob: (jobId, { recipient }) =>
      client
        .post(`/api/reports/jobs/${encodeURIComponent(jobId)}/email`, { recipient })
        .then(unwrap),
    resultUrl: (jobId) => `/api/reports/jobs/${encodeURIComponent(jobId)}/result`
  }
};

export default endpoints;
